/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import listItems from 'flarum/common/helpers/listItems';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import app from 'flarum/forum/app';
import { slug } from '../../../common';
import LinkButton from 'flarum/common/components/LinkButton';
import icon from 'flarum/common/helpers/icon';
import SelectDropdown from 'flarum/common/components/SelectDropdown';

// Make translation calls shorter
const t = (...args) => app.translator.trans(...args);
const prfx = `${slug}.forum.leaderboard.toolbar`;

export default class LeaderboardToolbar extends Component {
	oninit(vnode) {
		super.oninit(vnode);
	}

	view() {
		return (
			<div className="LeaderboardToolbar IndexPage-toolbar">
				<ul className="IndexPage-toolbar-view">{listItems(this.viewItems().toArray())}</ul>
				<ul className="IndexPage-toolbar-action">{listItems(this.actionItems().toArray())}</ul>
			</div>
		);
	}

	get currentParams() {
		let currentParams = m.route.param();
		currentParams = { ...currentParams }; // Just clone the object, else we remove the key below, and break things...
		delete currentParams.key; // Key comes from Mithril (?) and is not needed here
		return currentParams;
	}

	/**
	 * @return {ItemList}
	 */
	viewItems() {
		const items = new ItemList();

		const timeSpans = ['day', 'week', 'month', 'year', 'all'];

		const currentTimeSpan = this.currentParams.timeSpan || 'month';

		const hideTimeFilter = app.forum.attribute(`${slug}.leaderboard.hide_time_filter`) || false;

		if (!hideTimeFilter) {
			items.add(
				'time',
				<Dropdown buttonClassName="Button" label={t(`${prfx}.time_spans.${currentTimeSpan}`)}>
					{timeSpans.map((timeSpan) => {
						const active = timeSpan === currentTimeSpan;

						const params = {
							...this.currentParams,
							timeSpan: timeSpan,
						};

						if (timeSpan === 'month') {
							// Remove the time span if it is the month, as it is the default
							delete params.timeSpan;
						}

						return (
							<LinkButton icon={active ? 'fas fa-check' : true} active={active} href={app.route(app.current.data.routeName, params)}>
								{t(`${prfx}.time_spans.${timeSpan}`)}
							</LinkButton>
						);
					})}
				</Dropdown>,
				100
			);
		}

		const hideGroupFilter = app.forum.attribute(`${slug}.leaderboard.hide_group_filter`) || false;

		if (!hideGroupFilter) {
			items.add(
				'groupFilter',
				<SelectDropdown defaultLabel={t(`${prfx}.group_filter_button`)} caretIcon={'fas fa-caret-down'} buttonClassName="Button">
					{this.groupItems().toArray()}
				</SelectDropdown>,
				80
			);
		}

		const hideLevelFilter = app.forum.attribute(`${slug}.leaderboard.hide_level_filter`) || false;

		if (!hideLevelFilter) {
			items.add(
				'levelFilter',
				<SelectDropdown defaultLabel={t(`${prfx}.level_filter_button`)} caretIcon={'fas fa-caret-down'} buttonClassName="Button">
					{this.levelItems().toArray()}
				</SelectDropdown>,
				70
			);
		}

		return items;
	}

	groupItems() {
		const items = new ItemList();

		const currentGroupId = this.currentParams.group || null;

		const allGroupsParams = {
			...this.currentParams,
		};
		delete allGroupsParams.group; // Make sure the group is not set
		items.add(
			'all',
			<LinkButton
				icon={!currentGroupId ? 'fas fa-check' : true}
				active={!currentGroupId}
				href={app.route(app.current.data.routeName, allGroupsParams)}
			>
				{t(`${prfx}.all_groups`)}
			</LinkButton>
		);

		app.store
			.all('groups')
			.filter((group) => group.id() !== '2' && group.id() !== '3') // Hide members & guests
			.forEach((group) => {
				const groupId = group.id();
				const groupName = group.namePlural();
				const active = groupId === currentGroupId;

				const params = {
					...this.currentParams,
					group: groupId,
				};

				items.add(
					groupName,
					<LinkButton icon={active ? 'fas fa-check' : true} active={active} href={app.route(app.current.data.routeName, params)}>
						<span style={{ color: group.color() }}>
							{icon(group.icon())} {groupName}
						</span>
					</LinkButton>
				);
			});

		return items;
	}

	levelItems() {
		const items = new ItemList();

		const currentLevel = this.currentParams.level || null;

		const allLevelsParams = {
			...this.currentParams,
		};
		delete allLevelsParams.level; // Make sure the level is not set
		items.add(
			'all',
			<LinkButton icon={!currentLevel ? 'fas fa-check' : true} active={!currentLevel} href={app.route(app.current.data.routeName, allLevelsParams)}>
				{t(`${prfx}.all_levels`)}
			</LinkButton>
		);

		// 10 levels, from 1 to 10 as strings
		const levels = Array.from(Array(10).keys()).map((i) => (i + 1).toString());

		levels.forEach((level) => {
			const active = level === currentLevel;

			const params = {
				...this.currentParams,
				level: level,
			};

			items.add(
				`level-${level}`,
				<LinkButton icon={active ? 'fas fa-check' : true} active={active} href={app.route(app.current.data.routeName, params)}>
					{extractText(t(`${prfx}.level`, { level }))}
				</LinkButton>
			);
		});

		extractText;

		return items;
	}

	/**
	 * @return {ItemList}
	 */
	actionItems() {
		const items = new ItemList();

		items.add(
			'refresh',
			<Button
				title={t('core.forum.index.refresh_tooltip')}
				icon={'fas fa-sync'}
				className={'Button Button--icon'}
				onclick={() => {
					app.leaderboard.refresh();
				}}
			></Button>
		);

		return items;
	}
}
